import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import API from '../services/api';
import Comment from './Comment';
import '../pages/Sohbet.css';

function formatDate(dateString) {
    if (!dateString) return 'Yakın zamanda';
    return new Date(dateString).toLocaleDateString('tr-TR', {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
}

function SohbetDetail({ user }) {
    const navigate = useNavigate();
    const { id } = useParams();

    const [topic, setTopic] = useState(null);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [newCommentText, setNewCommentText] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchTopicDetail = useCallback(async () => {
        try {
            setLoading(true);
            const [topicRes, commentsRes] = await Promise.all([
                API.get(`/posts/${id}`),
                API.get(`/posts/${id}/comments`)
            ]);
            setTopic(topicRes.data);
            setComments(commentsRes.data || []);
        } catch (err) {
            setError(err.message || "Konu yüklenirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchTopicDetail();
    }, [fetchTopicDetail]);

    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        if (!newCommentText.trim()) return;

        if (!user) {
            alert("Yanıt yazmak için giriş yapmalısın.");
            return;
        }

        try {
            const response = await API.post(`/posts/${id}/comments`, {
                body: newCommentText.trim(),
                parent_id: null
            });

            const newComment = {
                ...response.data,
                author_username: user.username,
                author_avatar: user.avatar,
                replies: []
            };

            setComments(prev => [...prev, newComment]);
            setNewCommentText("");
            setTopic(prev => prev ? { ...prev, comment_count: (prev.comment_count || 0) + 1 } : prev);
        } catch (err) {
            alert("Mesaj gönderilirken hata oluştu: " + err.message);
        }
    };

    const handleReplySubmit = async (targetCommentId, replyText) => {
        if (!replyText.trim()) return;

        if (!user) {
            alert("Yanıt yazmak için giriş yapmalısın.");
            return;
        }

        try {
            const response = await API.post(`/posts/${id}/comments`, {
                body: replyText.trim(),
                parent_id: targetCommentId
            });

            const newReplyObj = {
                ...response.data,
                author_username: user.username,
                author_avatar: user.avatar,
                replies: []
            };

            const addReplyRecursive = (list) => list.map(c => {
                if (c.id === targetCommentId) {
                    return { ...c, replies: [...(c.replies || []), newReplyObj] };
                }
                if (c.replies && c.replies.length > 0) {
                    return { ...c, replies: addReplyRecursive(c.replies) };
                }
                return c;
            });

            setComments(prev => addReplyRecursive(prev));
            setTopic(prev => prev ? { ...prev, comment_count: (prev.comment_count || 0) + 1 } : prev);
        } catch (err) {
            alert("Yanıt gönderilirken hata oluştu: " + err.message);
        }
    };

    // Konuyu silme (sadece konuyu açan kişi görebilir/yapabilir) — sohbet
    // konusu backend'de zaten normal bir post, aynı uç kullanılıyor.
    const handleDeleteTopic = async () => {
        if (!window.confirm("Bu sohbet konusunu silmek istediğine emin misin? Bu işlem geri alınamaz.")) {
            return;
        }
        try {
            setIsDeleting(true);
            await API.delete(`/posts/${id}`);
            navigate('/sohbet');
        } catch (err) {
            alert("Konu silinirken hata oluştu: " + err.message);
            setIsDeleting(false);
        }
    };

    if (loading) return <div className="sohbet-forum-page">Yükleniyor...</div>;
    if (error || !topic) return <div className="sohbet-forum-page">{error || "Konu bulunamadı."}</div>;

    const authorName = topic.author?.username || (typeof topic.author === 'string' ? topic.author : null) || 'Anonim';
    const isOwner = Boolean(user && topic.author_id && user.id === topic.author_id);

    return (
        <div className="sohbet-forum-page">
            {/* Geri Dön Butonu */}
            <button className="sohbet-back-btn" onClick={() => navigate('/sohbet')}>
                ← Sohbet Odalarına Dön
            </button>

            {/* Ana Başlık Kartı */}
            <div className="sohbet-detail-header-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                    <h1 style={{ marginTop: '10px', marginBottom: '10px' }}>{topic.title}</h1>

                    {/* Sadece konuyu açan kişi görür */}
                    {isOwner && (
                        <button
                            onClick={handleDeleteTopic}
                            disabled={isDeleting}
                            style={{
                                background: 'none',
                                border: '1px solid var(--danger)',
                                color: 'var(--danger)',
                                borderRadius: '4px',
                                padding: '6px 12px',
                                fontSize: '13px',
                                cursor: isDeleting ? 'not-allowed' : 'pointer',
                                whiteSpace: 'nowrap',
                                marginTop: '10px'
                            }}
                        >
                            {isDeleting ? 'Siliniyor...' : '🗑 Konuyu Sil'}
                        </button>
                    )}
                </div>
                <div className="topic-row-meta">
                    <span>Başlatan: <strong>@{authorName}</strong></span> • <span>{formatDate(topic.created_at)}</span>
                </div>
            </div>

            {/* Yorumlar Bölümü */}
            <div className="sohbet-comments-section">
                <h3>💬 {topic.comment_count ?? comments.length} Yanıt</h3>

                <form className="sohbet-comment-form" onSubmit={handleCommentSubmit}>
                    <textarea
                        placeholder={user ? "Bu tartışmaya sen de katıl, fikrini yaz..." : "Yanıt yazmak için giriş yapmalısın"}
                        value={newCommentText}
                        onChange={(e) => setNewCommentText(e.target.value)}
                        disabled={!user}
                    />
                    <button type="submit" disabled={!user || !newCommentText.trim()}>
                        Yanıtı Gönder
                    </button>
                </form>

                <div className="sohbet-comments-list" style={{ marginTop: '20px' }}>
                    {comments.length === 0 && (
                        <p style={{ color: 'var(--muted)', padding: '16px 0' }}>Bu konuya henüz kimse yanıt yazmamış. İlk yanıtı sen yaz!</p>
                    )}
                    {comments.map(comment => (
                        <Comment
                            key={comment.id}
                            comment={comment}
                            onReplySubmit={handleReplySubmit}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

export default SohbetDetail;